import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { escape } from 'lodash';
import { Badge, Button } from 'reactstrap';
import { finalize, switchMap } from 'rxjs/operators';
import { RegionAPI } from '@business/api/region';
import { DealResponse } from '@business/entities/deal';
import { DealAvailabilityResponse, RepAvailabilityStatus, RepTravel, RepTravelLeg } from '@business/entities/region';
import { handleMessage, requestMessage } from '@business/messages';
import { UserService } from '@business/services';
import { isRegionsAccount } from '@business/services/common_service/common_service';
import { Session } from '@modules/auth/session';
import { showConfirmation } from '@modules/common';
import { InfoBanner } from '@modules/common/InfoBanner';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';

const BADGE: Record<RepAvailabilityStatus, { label: string; className: string }> = {
  available: { label: 'Available', className: 'badge-soft-success' },
  unavailable: { label: 'Unavailable', className: 'badge-soft-danger' },
  unknown: { label: 'Unknown', className: 'badge-soft-warning' },
};

const DURATIONS = [30, 60, 90, 120];

function drive(leg: RepTravelLeg) {
  const minutes = Math.round((leg.seconds || 0) / 60);
  const time = minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`;
  return `${time} · ${((leg.meters || 0) / 1000).toFixed(1)} km`;
}

function legText(leg: RepTravelLeg, direction: 'from' | 'to') {
  const when = toMoment(leg.at).format('h:mm A');
  const place = direction === 'from' ? `from the appointment ending ${when}` : `to the ${when} appointment`;
  if (leg.status === 'ok') return `${drive(leg)} ${place}`;
  if (leg.status === 'no_route') return `No driving route ${place}`;
  return `Travel time ${place} couldn't be retrieved`;
}

/** Drive from where the rep will be before the slot, and on to their next appointment. */
function travelText(travel: RepTravel) {
  const parts: string[] = [];
  if (travel.from) parts.push(legText(travel.from, 'from'));
  else if (travel.note) parts.push(travel.note);
  if (travel.to) parts.push(`then ${legText(travel.to, 'to')}`);
  return parts.join('; ');
}

function slotLabel(start: number, end: number) {
  return `${toMoment(start).format('ddd, MMM D, h:mm A')} – ${toMoment(end).format('h:mm A')}`;
}

export interface DealRepAvailabilityProps {
  deal: DealResponse;
  /** Reload the deal after a representative was assigned here. */
  onChanged(): void;
}

/**
 * Whether each representative of the deal's territory can take the deal's
 * appointment (or another time picked here). "Unknown" means nothing rules
 * them out but a check couldn't be made; it is never shown as available.
 * The best one is marked Selected and an admin assigns them with one click;
 * when nobody can be selected the exception is shown for a person to settle.
 */
export const DealRepAvailability = memo(({ deal, onChanged }: DealRepAvailabilityProps) => {
  const canManage = useMemo(() => !!Session.user && UserService.isAdmin(Session.user), []);
  const [version, setVersion] = useState(0);
  const [result, setResult] = useState<DealAvailabilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [custom, setCustom] = useState<{ start: number; end: number } | null>(null);
  const [picking, setPicking] = useState(false);
  const [pickStart, setPickStart] = useState(() => toMoment().add(1, 'day').startOf('hour').hour(10).format('YYYY-MM-DDTHH:mm'));
  const [pickMinutes, setPickMinutes] = useState(60);

  const enabled = isRegionsAccount();

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    const subscription = RegionAPI.fetchDealAvailability(deal.id, custom || undefined)
      .pipe(finalize(() => setLoading(false)))
      .subscribe(
        data => setResult(data),
        () => setResult(null),
      );
    return () => subscription.unsubscribe();
  }, [custom, deal.id, deal.regionId, deal.regionStatus, enabled, version]);

  const check = useCallback(() => {
    const start = toMoment(pickStart).unix();
    setCustom({ start, end: start + pickMinutes * 60 });
    setPicking(false);
  }, [pickMinutes, pickStart]);

  const assign = useCallback(() => {
    const slot = result?.slot;
    const selection = result?.selection;
    if (!slot || !selection) return;

    const name = `<b>${escape(selection.name)}</b>`;
    const others = (slot.team || []).filter(member => R.toString(member.userId) !== R.toString(selection.userId));
    const replaced = others.filter(member => member.representative).map(member => `<b>${escape(member.name)}</b>`);
    const stays = others.filter(member => !member.representative).map(member => `<b>${escape(member.name)}</b>`);
    const when = escape(slotLabel(slot.start, slot.end));

    let message =
      selection.onAppointment && !replaced.length
        ? `Record ${name} as this deal's representative? They're already on the ${when} appointment.`
        : `Put ${name} on the ${when} appointment${replaced.length ? ` in place of ${replaced.join(', ')}` : ''}?`;
    if (stays.length) message += ` ${stays.join(', ')} ${stays.length > 1 ? 'stay' : 'stays'} on it.`;
    if (!selection.onAppointment || replaced.length) {
      message += ' The appointment is updated like any other edit: calendars are synced and the invitation is emailed.';
    }

    showConfirmation('Assign Representative', message)
      .pipe(
        switchMap(() => RegionAPI.assignDealRepresentative(deal.id, R.toString(selection.userId))),
        handleMessage({ type: requestMessage('assign_deal_representative') }),
      )
      .subscribe(
        () => {
          onChanged();
          setVersion(v => v + 1);
        },
        // e.g. the pick changed since this was loaded: show the current one.
        () => setVersion(v => v + 1),
      );
  }, [deal.id, onChanged, result]);

  if (!enabled) return null;

  const slot = result?.slot;
  const selection = result?.selection;
  const canAssign = canManage && slot?.source === 'appointment' && !!selection && !selection.assigned;

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
        <span className="text-muted font-size-12">Representative Availability</span>
        <span className="font-size-12">
          {slot
            ? `${slotLabel(slot.start, slot.end)} · ${slot.source === 'appointment' ? 'booked appointment' : 'time picked here'}`
            : !loading && result
            ? 'No appointment booked for this deal'
            : ''}
        </span>
        {(!result || !!result.reps.length) && (
          <a className="text-primary font-size-12" onClick={() => setPicking(!picking)}>
            {picking ? 'Cancel' : 'Check another time'}
          </a>
        )}
        {custom && !picking && (
          <a className="text-primary font-size-12" onClick={() => setCustom(null)}>
            Back to the appointment
          </a>
        )}
      </div>

      {picking && (
        <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
          <input
            type="datetime-local"
            className="form-control form-control-sm"
            style={{ maxWidth: 220 }}
            value={pickStart}
            onChange={e => setPickStart(e.target.value)}
          />
          <select
            className="form-control form-control-sm"
            style={{ maxWidth: 120 }}
            value={pickMinutes}
            onChange={e => setPickMinutes(Number(e.target.value))}
          >
            {DURATIONS.map(m => (
              <option key={m} value={m}>
                {m < 60 ? `${m} min` : `${m / 60} h`}
              </option>
            ))}
          </select>
          <Button type="button" size="sm" color="primary" disabled={!pickStart} onClick={check}>
            Check
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-muted font-size-12">Checking calendars…</div>
      ) : !result ? (
        <div className="text-muted font-size-12">Availability couldn&apos;t be loaded.</div>
      ) : (
        <>
          {!!result.exception && (
            <InfoBanner intent="warning" noMargin className="mb-2">
              Needs a manual decision: {result.exception}
            </InfoBanner>
          )}
          {result.reps.map(rep => (
            <div key={rep.userId} className="d-flex align-items-center flex-wrap py-1" style={{ gap: 8 }}>
              <Badge className={`${BADGE[rep.status].className} font-size-11`} style={{ minWidth: 78 }}>
                {BADGE[rep.status].label}
              </Badge>
              <span className="font-size-13 font-weight-semibold">{rep.name}</span>
              {rep.selected && (
                <Badge className="badge-soft-primary font-size-11">
                  <i className="bx bx-check mr-1" />
                  Selected
                </Badge>
              )}
              {!!rep.territories?.length && <span className="font-size-12">{rep.territories.join(', ')}</span>}
              <span className="text-muted font-size-12">{[...rep.reasons, ...rep.unknowns].join(' · ')}</span>
              {rep.selected && canAssign && (
                <Button type="button" size="sm" color="primary" className="py-0" onClick={assign}>
                  Assign
                </Button>
              )}
              {rep.selected && selection?.assigned && (
                <span className="text-success font-size-12">
                  <i className="bx bx-check-circle mr-1" />
                  Assigned to this deal
                </span>
              )}
              {rep.selected && !!selection && (
                <div className="w-100 font-size-12 text-primary" style={{ paddingLeft: 86 }}>
                  <i className="bx bx-star mr-1" />
                  {selection.reason}
                  {slot?.source === 'custom' && <span className="text-muted"> · assigning is done on the booked appointment</span>}
                </div>
              )}
              {!!rep.excludedBecause && (
                <div className="w-100 font-size-12 text-warning" style={{ paddingLeft: 86 }}>
                  <i className="bx bx-error-circle mr-1" />
                  Not selected: {rep.excludedBecause}
                </div>
              )}
              {!!rep.travel && (
                <div className="w-100 text-muted font-size-12" style={{ paddingLeft: 86 }}>
                  <i className="bx bx-car mr-1" />
                  {travelText(rep.travel)}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
});
